import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NotificationInboxStore } from '../../store/notification-inbox.store';
import { InboxNotification } from '../../notification.model';

/** Unread-count poll interval. */
const POLL_MS = 60_000;

/**
 * The header notification bell.
 *
 * Polls the unread count rather than subscribing: there is no WebSocket or SSE
 * anywhere in this app, and every existing realtime surface is a `setInterval`.
 * The interval is owned here rather than in the store so it stops when the
 * header is destroyed instead of running for the lifetime of the tab.
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, OverlayModule, MatButtonModule, MatIconModule],
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  store = inject(NotificationInboxStore);
  private router = inject(Router);

  isOpen = signal(false);
  private timer: any = null;

  ngOnInit(): void {
    this.store.refreshUnread();
    this.timer = setInterval(() => this.store.refreshUnread(), POLL_MS);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  toggle(): void {
    const opening = !this.isOpen();
    this.isOpen.set(opening);
    // Load the list only when the panel is actually opened — the badge poll is
    // a single count, and pulling ten rows every minute for a closed panel
    // would be pure waste.
    if (opening) this.store.loadInbox();
  }

  /**
   * Follow a notification's click-to-view link.
   *
   * Prefers the in-app path: the user is already authenticated here, so sending
   * them to a signed public link would take them out of the app for no reason.
   * `linkUrl` (the signed form) is the fallback for anything with no app path.
   */
  open(item: InboxNotification): void {
    this.store.markRead(item.notificationId);
    const appPath = item.deepLink?.appPath;
    if (appPath) {
      this.isOpen.set(false);
      void this.router.navigateByUrl(appPath);
    } else if (item.linkUrl) {
      window.open(item.linkUrl, '_blank', 'noopener');
    }
  }

  markAllRead(): void {
    this.store.markAllRead();
  }

  /**
   * Empties the panel. Clears read AND unread entries — "clear" means the list
   * goes away, and leaving unread ones behind would make the button look broken.
   * Nothing is destroyed: the notifications stay in the profile-wide log, they
   * just stop appearing in this user's inbox.
   */
  clearAll(): void {
    this.store.dismissAll();
    this.isOpen.set(false);
  }

  severityClass(item: InboxNotification): string {
    return `severity--${(item.severity ?? 'INFO').toLowerCase()}`;
  }
}
